import dayjs from 'dayjs';
import { PropTypes } from 'prop-types';
import React, { useCallback, useMemo, useRef, useEffect, forwardRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useHistory } from 'react-router-dom';

import { Checkbox, CheckedState } from '@ge/components/checkbox';
import { ConditionalRender } from '@ge/components/conditional-render';
import { placements, Tooltip } from '@ge/components/tooltip';
import { useManualAdjustmentContext } from '@ge/feat-analyze/context/manual-adjustment-context';
import { AvailabilityKpi } from '@ge/feat-analyze/models/manual-adjustment-defs';
import { Placeholders } from '@ge/models/constants';

import {
  Row,
  Cell,
  SelectionColumn,
  ChartRow,
  EndingColumn,
  AssetKPI,
  ChartBar,
  EntityCell,
  EntityName,
  EntityArrow,
} from './site-panel-chart-style';

const getColumnStartDate = (startDate, currentDate, timezone) => {
  const tempStartDate = dayjs(startDate).tz(timezone).startOf('day');
  const tempCurrentDate = dayjs(currentDate).tz(timezone).startOf('day');
  if (!tempStartDate.isSame(tempCurrentDate, 'day')) {
    return 0;
  } else {
    return (startDate - currentDate) / 1000;
  }
};

const getBarStyle = (e, startOfDay, timezone) => {
  const columnStart = getColumnStartDate(e.startTime, startOfDay, timezone) || 1;
  const columnEnd = (e.endTime - startOfDay) / 1000;

  return {
    gridColumnStart: columnStart,
    gridColumnEnd: columnEnd,
    backgroundColor: e.color,
    marginTop: e.type === 'MAL' ? '-40px' : '',
  };
};

const getMALBarStyle = (e, startOfDay, isSelected, timezone, name) => {
  const columnStart = getColumnStartDate(e.startTime, startOfDay, timezone, name) || 1;
  const columnEnd = (e.endTime - startOfDay) / 1000;

  return {
    gridColumnStart: columnStart,
    gridColumnEnd: columnEnd,
    backgroundColor: '#C15A47',
    marginTop: isSelected ? '37px' : '17px',
    height: '12px',
    backgroundImage: 'linear-gradient(#782D1E 75%, #C15A47 85%, #DB8585 100%)',
    WebkitMaskImage:
      '-webkit-gradient(linear, left top, left bottom, from(rgba(0,0,0,0)), to(rgba(0,0,0,1)))',
  };
};

const SitePanelChartRow = forwardRef(({ data, site, startOfDay, allAssets }, ref) => {
  const chartRowRef = useRef(null);
  const location = useLocation();
  const history = useHistory();
  const firstTimeAssetSelectedRef = useRef(null);
  const firstTimeEventSelectedRef = useRef(null);

  const {
    selectedEvent: selected,
    setSelectedEvent,
    selectedAssets,
    setSelectedAssets,
    autoWidth,
    calendarDate,
  } = useManualAdjustmentContext();

  const isSelected = useMemo(() => {
    if (location?.state?.assetId === data.asset.id && !firstTimeAssetSelectedRef.current) {
      firstTimeAssetSelectedRef.current = true;
      return true;
    } else return data.asset.id === selected?.id && !selected.bulkEdit;
  }, [data?.asset?.id, location?.state?.assetId, selected?.bulkEdit, selected?.id]);

  const eventData = [...data.assetEvents, ...data.malEvents];

  const kpiCells = useCallback(
    () =>
      Object.values(AvailabilityKpi).map((def) => {
        const kpiValue = data.availability[def.id];
        if (kpiValue === null || kpiValue === undefined || kpiValue === '') {
          return <AssetKPI key={def.id}>{Placeholders.DOUBLE_DASH}</AssetKPI>;
        }
        return <AssetKPI key={def.id}>{`${kpiValue}${def.unit}`}</AssetKPI>;
      }),
    [data],
  );

  const handleSelectAsset = useCallback(
    (checked) => {
      setSelectedAssets((prev) =>
        checked ? [...prev, data.asset.id] : prev.filter((id) => id !== data.asset.id),
      );
    },
    [data, setSelectedAssets],
  );

  const handleSelectEvent = useCallback(
    (e) => {
      const multiAssets = allAssets
        .filter((assetRow) =>
          assetRow.malEvents.find((malEvent) => malEvent.malId === e.event.malId),
        )
        .map((assetRow) => assetRow.asset.id);
      setSelectedAssets(multiAssets.length > 1 ? multiAssets : []);
      setSelectedEvent({ ...e, bulkEdit: multiAssets.length > 1 });
      if (!e.isPreserveHistory) {
        history.push({
          pathname: history.location.pathname,
          state: null,
        });
      }
    },
    [allAssets, history, setSelectedAssets, setSelectedEvent],
  );

  useEffect(() => {
    if (
      location?.state?.malId &&
      !firstTimeEventSelectedRef.current &&
      data?.malEvents?.length > 0 &&
      data?.asset
    ) {
      const tempMalId = location?.state?.malId;
      const event = data.malEvents.find((event) => event.malId === tempMalId);
      if (event) handleSelectEvent({ id: data.asset?.id, site, event, isPreserveHistory: true });
      firstTimeEventSelectedRef.current = true;
    }
  }, [
    data.asset,
    data.malEvents,
    data.malEvents?.length,
    handleSelectEvent,
    location?.state?.malId,
    site,
  ]);

  return (
    <Row>
      <Cell ref={ref} isSelected={isSelected}>
        <SelectionColumn>
          <Checkbox
            checkState={
              selectedAssets.includes(data.asset?.id)
                ? CheckedState.CHECKED
                : CheckedState.UNCHECKED
            }
            label={''}
            onChange={handleSelectAsset}
          />
        </SelectionColumn>
        <EntityCell>
          <EntityName
            onClick={() => {
              history.push({
                pathname: `asset-overview`,
                search: `?assets=${data.asset?.id}`,
                state: {
                  endDate: calendarDate,
                  startDate: dayjs(calendarDate).startOf('date').subtract(1, 'day').valueOf(),
                },
              });
            }}
          >
            {data.asset?.name}
          </EntityName>
          <EntityArrow />
        </EntityCell>
        <ChartRow numberOfColumns={86400}>
          {eventData.map((event, i) => (
            <Tooltip
              key={i}
              title={event.type === 'IEC' ? event.oemDescription : event.malCategoryName}
              placement={placements.TOP}
            >
              <ChartBar
                ref={chartRowRef}
                isClicked={selected?.event?.malId === event.malId}
                isSelected={isSelected}
                style={getBarStyle(event, startOfDay, site.timezone)}
                onClick={() => {
                  handleSelectEvent({ id: data.asset?.id, site, event, isPreserveHistory: false });
                }}
              >
                <ConditionalRender shouldRender={event.type === 'MAL'}>
                  <ChartBar
                    style={getMALBarStyle(event, startOfDay, isSelected, site.timezone)}
                    eventType={event.type}
                  />
                </ConditionalRender>
              </ChartBar>
            </Tooltip>
          ))}
        </ChartRow>
      </Cell>
      <EndingColumn autoWidth={autoWidth}>{kpiCells()}</EndingColumn>
    </Row>
  );
});

SitePanelChartRow.propTypes = {
  startOfDay: PropTypes.number.isRequired,
  data: PropTypes.object.isRequired,
  site: PropTypes.instanceOf(Object),
  allAssets: PropTypes.array,
};

SitePanelChartRow.displayName = 'SitePanelChartRow';

export default SitePanelChartRow;
